import * as Auth from "@aws-amplify/auth";
import { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";

/**
 * Component to protect routes, depending on Cognito authenticated
 */
const PrivateRoute = ({ children }: any) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const getRememberMe = (): boolean => {
    return (
      localStorage.getItem("rememberMe") === "true" ||
      sessionStorage.getItem("rememberMe") === "true"
    );
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("rememberMe");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("rememberMe");
  };

  const handleAuthorizationResponse = async (isMounted: boolean) => {
    // In Amplify v6, use fetchAuthSession() to get the current session
    try {
      const session = await Auth.fetchAuthSession();
      const token = session?.tokens?.idToken;

      // Validate that we have a valid token with expiration
      if (!token?.payload?.exp) {
        // No valid token found
        clearAuthStorage();
        if (isMounted) {
          setIsLoaded(true);
        }
        return;
      }

      // Token expiration check: compare epoch time (both are seconds since epoch)
      // No manual offset needed - AWS Cognito tokens use standard JWT format
      const isExpired = token.payload.exp < Date.now() / 1000;

      const rememberMe = getRememberMe();

      if (isExpired) {
        // Token is expired - sign out user
        // Note: fetchAuthSession() does NOT refresh expired tokens in Amplify v6
        await Auth.signOut();
        clearAuthStorage();
      } else {
        // Token is valid, user is authenticated
        if (isMounted) {
          setIsAuthenticated(true);
          setIsLoaded(true);
        }
      }
    } catch (err) {
      // Error getting session, user is not authenticated
      clearAuthStorage();
      if (isMounted) {
        setIsLoaded(true);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (!pathname) return;

    // Retrieve user credentials - in Amplify v6, use getCurrentUser()
    const handleAuthorization = async () => {
      try {
        const user = await Auth.getCurrentUser();
        if (user) {
          await handleAuthorizationResponse(isMounted);
        } else {
          // No user found, navigate to login
          clearAuthStorage();
          navigate("/login", { state: { prevPath: pathname } });
        }
      } catch (err) {
        // Clear local data and navigate to login, with attempted path
        clearAuthStorage();
        navigate("/login", { state: { prevPath: pathname } });
      }
    };

    handleAuthorization();
    return () => {
      isMounted = false;
    };
  }, [navigate, pathname]);

  if (!isLoaded) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ prevPath: pathname }} />;
  }
  return children;
};

export default PrivateRoute;
